import { useSyncExternalStore, useCallback } from 'react';
import type { SiteWeather } from '@/lib/weather';
import type { CommunityReport } from '@/stores/reportsStore';

/* ── Types ── */
export interface SavedSiteData {
  siteId: string;
  savedAt: string; // ISO
  weatherCache?: SiteWeather | null;
}

export interface PendingReport extends Omit<CommunityReport, 'id' | 'timestamp'> {
  localId: string;
  createdAt: string; // ISO
  synced: boolean;
}

/* ── Storage helpers ── */
const SAVED_KEY = 'divecheck_saved_sites';
const PENDING_KEY = 'divecheck_pending_reports';
const WEATHER_KEY = 'divecheck_weather_cache';

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

/* ── Reactive store (external store pattern) ── */
let savedSites: SavedSiteData[] = readJSON(SAVED_KEY, []);
let pendingReports: PendingReport[] = readJSON(PENDING_KEY, []);
let weatherCache: Record<string, { data: SiteWeather; cachedAt: number }> = readJSON(WEATHER_KEY, {});

let listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/* ── Saved sites ── */
export const saveSiteOffline = (siteId: string, weather?: SiteWeather | null) => {
  if (savedSites.some((s) => s.siteId === siteId)) return;
  savedSites = [...savedSites, { siteId, savedAt: new Date().toISOString(), weatherCache: weather }];
  writeJSON(SAVED_KEY, savedSites);
  emit();
};

export const removeSiteOffline = (siteId: string) => {
  savedSites = savedSites.filter((s) => s.siteId !== siteId);
  writeJSON(SAVED_KEY, savedSites);
  emit();
};

export const isSiteSaved = (siteId: string) => savedSites.some((s) => s.siteId === siteId);

export const getSavedSiteData = (siteId: string) => savedSites.find((s) => s.siteId === siteId);

/* ── Weather cache (persistent) ── */
export const cacheWeather = (siteId: string, data: SiteWeather) => {
  weatherCache = { ...weatherCache, [siteId]: { data, cachedAt: Date.now() } };
  writeJSON(WEATHER_KEY, weatherCache);
  // also update saved site weather if saved
  const idx = savedSites.findIndex((s) => s.siteId === siteId);
  if (idx >= 0) {
    savedSites = savedSites.map((s) => s.siteId === siteId ? { ...s, weatherCache: data } : s);
    writeJSON(SAVED_KEY, savedSites);
  }
  emit();
};

export const getCachedWeather = (siteId: string): { data: SiteWeather; cachedAt: number } | null => {
  return weatherCache[siteId] ?? null;
};

/* ── Pending reports (offline queue) ── */
export const addPendingReport = (report: Omit<PendingReport, 'localId' | 'createdAt' | 'synced'>) => {
  const pending: PendingReport = {
    ...report,
    localId: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    synced: false,
  };
  pendingReports = [pending, ...pendingReports];
  writeJSON(PENDING_KEY, pendingReports);
  emit();
  return pending;
};

export const markReportSynced = (localId: string) => {
  pendingReports = pendingReports.map((r) =>
    r.localId === localId ? { ...r, synced: true } : r
  );
  writeJSON(PENDING_KEY, pendingReports);
  emit();
};

export const removeSyncedReports = () => {
  pendingReports = pendingReports.filter((r) => !r.synced);
  writeJSON(PENDING_KEY, pendingReports);
  emit();
};

export const getPendingReports = () => pendingReports.filter((r) => !r.synced);

/* ── Hook: subscribe to changes ── */
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useOfflineStore = () => {
  const saved = useSyncExternalStore(subscribe, () => savedSites);
  const pending = useSyncExternalStore(subscribe, () => pendingReports);
  return { savedSites: saved, pendingReports: pending };
};

export const useSavedSite = (siteId: string) => {
  const { savedSites: all } = useOfflineStore();
  const isSaved = all.some((s) => s.siteId === siteId);
  const data = all.find((s) => s.siteId === siteId);

  const toggle = useCallback((weather?: SiteWeather | null) => {
    if (isSaved) removeSiteOffline(siteId);
    else saveSiteOffline(siteId, weather);
  }, [siteId, isSaved]);

  return { isSaved, data, toggle };
};
